import React, { useState, useEffect, useRef, useCallback } from 'react';

const CANVAS_RESOLUTION = 500; // Resolución virtual para alta nitidez en retina displays
const SCRATCH_RADIUS = 30;     // Radio de raspado en px
const TARGET_PERCENTAGE = 60;  // Porcentaje para auto-completar

export const MvpXolosZoneScratch = ({ onGameFinish, matchTitle = 'Club Tijuana Femenil' }) => {
    const canvasRef = useRef(null);
    const isDrawingRef = useRef(false);
    const lastPointRef = useRef(null);
    const hasCompletedRef = useRef(false);

    const [scratchedPercentage, setScratchedPercentage] = useState(0);
    const [isFinished, setIsFinished] = useState(false);
    const [statusMessage, setStatusMessage] = useState('¡Usa tu dedo para raspar la tarjeta!');

    // Inicializar y pintar la capa superior para raspar
    const initScratchCover = useCallback(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        canvas.width = CANVAS_RESOLUTION;
        canvas.height = CANVAS_RESOLUTION;

        // Gradiente metálico rojo y oscuro con estilo Xolos
        const grad = ctx.createLinearGradient(0, 0, CANVAS_RESOLUTION, CANVAS_RESOLUTION);
        grad.addColorStop(0, '#7f1d1d');   // Dark Red
        grad.addColorStop(0.3, '#dc2626'); // Red
        grad.addColorStop(0.5, '#ef4444'); // Light Red
        grad.addColorStop(0.7, '#991b1b'); // Darker Red
        grad.addColorStop(1, '#450a0a');   // Very Dark Red

        ctx.fillStyle = grad;
        ctx.fillRect(0, 0, CANVAS_RESOLUTION, CANVAS_RESOLUTION);

        // Borde decorativo
        ctx.strokeStyle = '#fca5a5';
        ctx.lineWidth = 12;
        ctx.strokeRect(10, 10, CANVAS_RESOLUTION - 20, CANVAS_RESOLUTION - 20);

        // Patrón sutil de puntos dorados/blancos
        ctx.fillStyle = 'rgba(255, 255, 255, 0.12)';
        for (let x = 25; x < CANVAS_RESOLUTION; x += 30) {
            for (let y = 25; y < CANVAS_RESOLUTION; y += 30) {
                ctx.beginPath();
                ctx.arc(x, y, 3, 0, Math.PI * 2);
                ctx.fill();
            }
        }

        // Ícono central
        ctx.fillStyle = '#ffffff';
        ctx.font = 'bold 36px sans-serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText('🐾 RASCA AQUÍ 🐾', CANVAS_RESOLUTION / 2, CANVAS_RESOLUTION / 2 - 40);

        ctx.font = '600 22px sans-serif';
        ctx.fillStyle = '#fef08a';
        ctx.fillText('Desliza tu dedo sobre la tarjeta', CANVAS_RESOLUTION / 2, CANVAS_RESOLUTION / 2 + 15);

        ctx.font = 'bold 18px sans-serif';
        ctx.fillStyle = 'rgba(255, 255, 255, 0.85)';
        ctx.fillText('¡Descubre tu mensaje especial!', CANVAS_RESOLUTION / 2, CANVAS_RESOLUTION / 2 + 65);
    }, []);

    useEffect(() => {
        initScratchCover();
    }, [initScratchCover]);

    // Calcular el porcentaje raspado mediante muestreo de canal alfa
    const calculatePercentage = useCallback(() => {
        const canvas = canvasRef.current;
        if (!canvas) return 0;
        const ctx = canvas.getContext('2d');
        if (!ctx) return 0;

        try {
            const imgData = ctx.getImageData(0, 0, CANVAS_RESOLUTION, CANVAS_RESOLUTION);
            const pixels = imgData.data;
            const step = 16; // Muestrear cada 16px para máximo rendimiento
            let transparentCount = 0;
            let totalSampled = 0;

            for (let y = 0; y < CANVAS_RESOLUTION; y += step) {
                for (let x = 0; x < CANVAS_RESOLUTION; x += step) {
                    const idx = (y * CANVAS_RESOLUTION + x) * 4;
                    const alpha = pixels[idx + 3];
                    if (alpha < 128) {
                        transparentCount++;
                    }
                    totalSampled++;
                }
            }

            const pct = Math.round((transparentCount / totalSampled) * 100);
            return pct;
        } catch (e) {
            return 0;
        }
    }, []);

    // Ejecutar el raspado en las coordenadas dadas con interpolación de línea
    const scratchAt = useCallback((x, y) => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        ctx.globalCompositeOperation = 'destination-out';
        ctx.beginPath();

        if (lastPointRef.current) {
            ctx.lineWidth = SCRATCH_RADIUS * 2;
            ctx.lineCap = 'round';
            ctx.lineJoin = 'round';
            ctx.moveTo(lastPointRef.current.x, lastPointRef.current.y);
            ctx.lineTo(x, y);
            ctx.stroke();
        }

        ctx.arc(x, y, SCRATCH_RADIUS, 0, Math.PI * 2);
        ctx.fill();

        lastPointRef.current = { x, y };

        // Feedback háptico ligero
        if (navigator.vibrate) {
            try { navigator.vibrate(10); } catch (e) {}
        }
    }, []);

    const getCanvasPoint = (e) => {
        const canvas = canvasRef.current;
        if (!canvas) return null;
        const rect = canvas.getBoundingClientRect();
        const clientX = e.touches ? e.touches[0].clientX : e.clientX;
        const clientY = e.touches ? e.touches[0].clientY : e.clientY;

        const scaleX = CANVAS_RESOLUTION / rect.width;
        const scaleY = CANVAS_RESOLUTION / rect.height;

        return {
            x: (clientX - rect.left) * scaleX,
            y: (clientY - rect.top) * scaleY
        };
    };

    const handlePointerDown = (e) => {
        if (isFinished) return;
        isDrawingRef.current = true;
        const pt = getCanvasPoint(e);
        if (pt) {
            lastPointRef.current = pt;
            scratchAt(pt.x, pt.y);
        }
    };

    const handlePointerMove = (e) => {
        if (!isDrawingRef.current || isFinished) return;
        const pt = getCanvasPoint(e);
        if (pt) {
            scratchAt(pt.x, pt.y);
        }
    };

    const handlePointerUp = () => {
        if (!isDrawingRef.current) return;
        isDrawingRef.current = false;
        lastPointRef.current = null;

        const pct = calculatePercentage();
        setScratchedPercentage(pct);

        if (pct >= TARGET_PERCENTAGE && !hasCompletedRef.current) {
            finishGame(pct);
        }
    };

    // Revelar por completo y finalizar
    const finishGame = (pct = 100) => {
        if (hasCompletedRef.current) return;
        hasCompletedRef.current = true;
        setIsFinished(true);
        setScratchedPercentage(100);
        setStatusMessage('✨ ¡Tarjeta descubierta con éxito!');

        // Limpiar el canvas para mostrar toda la imagen oculta
        const canvas = canvasRef.current;
        if (canvas) {
            const ctx = canvas.getContext('2d');
            if (ctx) {
                ctx.clearRect(0, 0, CANVAS_RESOLUTION, CANVAS_RESOLUTION);
            }
        }

        setTimeout(() => {
            if (onGameFinish) {
                onGameFinish({
                    gameType: 'SCRATCH',
                    success: true,
                    percentageScratched: Math.max(pct, TARGET_PERCENTAGE)
                });
            }
        }, 1200);
    };

    return (
        <div className="MvpXolosZoneScratch w-100 d-flex flex-column align-items-center justify-content-center p-3 text-white" style={{ minHeight: '65vh', userSelect: 'none' }}>
            {/* Header del Minijuego */}
            <div className="text-center mb-3">
                <div className="badge bg-danger px-3 py-2 fs-6 text-uppercase fw-bold shadow-sm mb-2">
                    <i className="bi bi-gift-fill me-2"></i>Rasca y Gana
                </div>
                <h4 className="fw-bold text-white mb-1" style={{ textShadow: '0 2px 8px rgba(0,0,0,0.8)' }}>
                    {statusMessage}
                </h4>
                <div className="d-flex justify-content-center align-items-center gap-2 mt-2">
                    <span className="small text-white-50 fw-semibold">Descubierto:</span>
                    <div className="progress" style={{ width: '130px', height: '10px', background: 'rgba(255,255,255,0.15)' }}>
                        <div
                            className="progress-bar bg-danger progress-bar-striped progress-bar-animated"
                            role="progressbar"
                            style={{ width: `${scratchedPercentage}%` }}
                        />
                    </div>
                    <span className="badge bg-dark border border-secondary text-white small">
                        {scratchedPercentage}%
                    </span>
                </div>
            </div>

            {/* Contenedor de la Tarjeta con Canvas Superpuesto */}
            <div
                className="position-relative rounded-4 overflow-hidden shadow-lg border border-2 border-danger"
                style={{
                    maxWidth: '340px',
                    width: '100%',
                    aspectRatio: '1/1',
                    boxShadow: '0 0 25px rgba(239, 68, 68, 0.4)'
                }}
            >
                {/* Capa Inferior: Contenido Revelable (Xolos Femenil) */}
                <div
                    className="position-absolute top-0 start-0 w-100 h-100 d-flex flex-column align-items-center justify-content-center p-4 text-center"
                    style={{
                        background: 'linear-gradient(135deg, #111827 0%, #000000 100%)',
                        color: '#ffffff'
                    }}
                >
                    <div
                        className="rounded-circle d-flex align-items-center justify-content-center mb-3 shadow"
                        style={{
                            width: '80px',
                            height: '80px',
                            background: 'radial-gradient(circle, #ef4444 0%, #991b1b 100%)',
                            border: '3px solid #fef08a'
                        }}
                    >
                        <i className="bi bi-trophy-fill text-warning fs-1"></i>
                    </div>
                    <h5 className="fw-bold text-danger text-uppercase mb-1" style={{ letterSpacing: '1px' }}>
                        ¡SOMOS PERROS!
                    </h5>
                    <p className="small text-white-50 mb-2 fw-semibold">
                        {matchTitle}
                    </p>
                    <div className="badge bg-danger-subtle text-danger px-3 py-2 rounded-pill small fw-bold">
                        <i className="bi bi-heart-fill text-danger me-1"></i>¡Gracias por tu pasión rojinegra!
                    </div>
                </div>

                {/* Capa Superior: Canvas Rascable */}
                <canvas
                    ref={canvasRef}
                    onMouseDown={handlePointerDown}
                    onMouseMove={handlePointerMove}
                    onMouseUp={handlePointerUp}
                    onTouchStart={handlePointerDown}
                    onTouchMove={handlePointerMove}
                    onTouchEnd={handlePointerUp}
                    className="position-absolute top-0 start-0 w-100 h-100"
                    style={{
                        touchAction: 'none',
                        cursor: isFinished ? 'default' : 'crosshair'
                    }}
                />
            </div>

            {/* Botón de Auto-completar */}
            {!isFinished && (
                <div className="mt-3 text-center animate__animated animate__fadeIn">
                    <button
                        type="button"
                        onClick={() => finishGame(100)}
                        className="btn btn-outline-light btn-sm rounded-pill px-3 py-1 shadow-sm opacity-75"
                        style={{ fontSize: '0.8rem' }}
                    >
                        <i className="bi bi-magic me-1"></i>Descubrir por completo
                    </button>
                </div>
            )}
        </div>
    );
};
